import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Header, Grid, Dropdown, DropdownProps, DropdownItemProps } from "semantic-ui-react";
import { ReactSVG } from "react-svg";
import { NeighborhoodViewModel } from '../../types/NeighborhoodDTO';
import { IdNameViewModel } from '../../types/ServerDTO';
import DataGrid, { Column } from '../Common/SharedComponents/DataGrid';
import { DynamicSearchResponse, FilterData } from '../../types/filterDataGridDTO';
import DocumentAdd from "../../assets/images/document-add.svg";
import UserInfo from '../Common/SharedComponents/UserInfo';
import AdminSidebar from '../Common/SharedComponents/AdminSidebar';
import AppContext, { token_config } from '../../AppContext';
import SwitchTheme from '../Common/SharedComponents/SwitchTheme';
import AxiosInstance from '../../helpers/axiosInstance';

const Neighborhoods: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo } = useContext(AppContext);

  useEffect(() => {
    if (userInfo !== undefined) {
      const privileges: string[] = userInfo ? userInfo.Privileges.split(',') : [];
      if (!privileges.includes(token_config.MANAGE_NEIGHBORHOODS)) {
        navigate('/');
      }
    }
  }, [userInfo, navigate]);

  const [responseDataModel, setResponseDataModel] = useState<DynamicSearchResponse<NeighborhoodViewModel> | null>(null);
  const [filters, setFilters] = useState<Record<string, FilterData> | null>(null);
  const [wardResponse, setWardResponse] = useState<any>(null);
  const [wardDropdownLeft, setWardDropdownLeft] = useState<number>(0);

  const columns: Column<NeighborhoodViewModel>[] = [
    { field: 'neighborhood', header: "Neighborhood", sortable: true, headerStyle: { textAlign: 'left', paddingLeft: 10, width: '50%' }, style: { textAlign: 'left', fontSize: '16px' }, accessor: (row: NeighborhoodViewModel) => row.neighborhood ? <Link to={`/neighborhood/${row.neighborhoodId}`}>{row.neighborhood}</Link> : null },
    { field: 'ward', header: "Ward", sortable: false, headerStyle: { textAlign: 'left', paddingLeft: 10, width: '30%' }, style: { textAlign: 'left', fontSize: '16px' }, accessor: (row: NeighborhoodViewModel) => row.ward },
    { field: 'status', header: "Status", sortable: true, headerStyle: { textAlign: 'left', paddingLeft: 10, width: '20%' }, style: { textAlign: 'left', fontSize: '16px' }, accessor: (row: NeighborhoodViewModel) => row.status },
  ];

  useEffect(() => {
    document.title = 'Neighborhoods - Adjure';
    const sectionInner = (document.querySelector('.section__inner div') as any).getBoundingClientRect();
    setWardDropdownLeft(sectionInner.width + sectionInner.left);
  }, []);

  const convertToDropDownProps = (MasterItems: Array<IdNameViewModel>) => {
    return (MasterItems || []).map((item: IdNameViewModel) => ({
      key: item.id,
      text: item.name,
      value: item?.id,
    } as DropdownItemProps));
  }

  const handleDropdownChange = async (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    const { value } = data;

    const postParameter = { ...responseDataModel };
    postParameter.wardId = value ? value : null;
    const response = await AxiosInstance.post(`/AdminSetting/NeighborhoodDataGrid`, postParameter);

    setResponseDataModel(response.data);
    setWardResponse(response);
  };

  return (
    <section className="section" style={{
      minHeight: '100vh'
    }}>
      <div className="section__head">
        <div className="section__head-content">
          <Header as="h1">Neighborhoods</Header>
        </div>
        <div className="section__actions">
          <UserInfo />
          {userInfo?.IsAllOffices !== "True" && (
            <Button color="violet" onClick={() => navigate('/neighborhood')}>
              <Button.Content visible style={{ whiteSpace: 'nowrap' }}>
                Add Neighborhood
              </Button.Content>
              <Button.Content visible>
                <ReactSVG src={DocumentAdd} />
              </Button.Content>
            </Button>
          )}
          <SwitchTheme />
        </div>
      </div>
      <div className="section__body">
        {responseDataModel?.wards && (
          <div className="section__actions" style={{ float: 'right', marginTop: '1rem', position: 'absolute', left: wardDropdownLeft + 'px', zIndex: 1, width: '200px' }}>
            <Dropdown
              fluid
              selection
              clearable
              name="wardId"
              placeholder="Search Ward"
              onChange={handleDropdownChange}
              options={convertToDropDownProps(responseDataModel?.wards)}
              value={responseDataModel?.wardId ? responseDataModel?.wardId : ''}
            />
          </div>
        )}
        <Grid padded>
          <Grid.Row>
            <Grid.Column mobile={16} tablet={3} computer={3}>
              <AdminSidebar />
            </Grid.Column>
            <Grid.Column mobile={16} tablet={13} computer={13}>
              <DataGrid<NeighborhoodViewModel>
                apiUrl={`/AdminSetting/NeighborhoodDataGrid`}
                initialViewModelURL={`/AdminSetting/GetNeighborhoodDataGrid`}
                columns={columns}
                rowsPerPage={10}
                defaultSortByField={'neighborhood'}
                defaultSortOrderDesc={false}
                rowKey={(row: NeighborhoodViewModel) => row.neighborhoodId}
                noDataMessage={'No neighborhood(s) to display'}
                searchPlaceholder='Search by neighborhood or ward'
                filters={filters}
                setFilters={setFilters}
                responseDataModel={responseDataModel}
                setResponseDataModel={setResponseDataModel}
                wardResponse={wardResponse}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </section>
  );
}

export default Neighborhoods;